// Take one part back off a built unit. The part goes to stock, to QC hold or
// to scrap; QC hold is the default because a part pulled off a drone has not
// been checked yet.

import React, { useEffect, useState } from 'react';
import { Segmented } from '../components/FilterBar';
import { buttonClass } from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';
import { TextArea } from '../components/ui/Field';
import { RadioGroup, RadioRow } from '../components/ui/Radio';
import { Alert } from '../components/ui/Alert';
import { Skeleton } from '../components/ui/Skeleton';
import FadeIn from '../components/ui/FadeIn';
import { useToast } from '../components/Toast/ToastContext';
import {
  DESTINATION_LABELS,
  useWorkOrdersApi,
  workOrderErrorMessage,
  type FittedPart,
  type RemovalDestination,
  type Unit,
} from './workOrdersApi';

interface Props {
  orderId: string;
  unit: Unit;
  onClose: () => void;
  onDone: () => void;
}

const PART_HEADING = <div className="mb-2 text-sm font-medium text-on-surface">Which part?</div>;

const DESTINATIONS: RemovalDestination[] = ['stock', 'qc_hold', 'scrap'];

const DESTINATION_HINTS: Record<RemovalDestination, string> = {
  stock: 'Back on the shelf, ready to use again.',
  qc_hold: 'Set aside until someone checks it.',
  scrap: 'Written off. It will not be used again.',
};

const RemovalDialog: React.FC<Props> = ({ orderId, unit, onClose, onDone }) => {
  const woApi = useWorkOrdersApi();
  const toast = useToast();
  const [parts, setParts] = useState<[string, FittedPart][] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [destination, setDestination] = useState<RemovalDestination>('qc_hold');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!unit.serial) return;
    let cancelled = false;
    woApi
      .fittedParts(unit.serial)
      .then((d) => {
        if (cancelled) return;
        const entries = Object.entries(d?.fitted || {});
        setParts(entries);
        if (entries.length === 1) setSlot(entries[0][0]);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(workOrderErrorMessage(err, 'The fitted parts did not load. Close this and try again.'));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit.serial]);

  const picked = parts?.find(([s]) => s === slot)?.[1] ?? null;

  // A lone fitted part is picked for the person, so only a pick among several counts as typed work.
  const dirty = reason !== '' || destination !== 'qc_hold' || (slot !== null && parts?.length !== 1);

  const submit = async () => {
    if (!picked) {
      setError('Pick the part you are taking off.');
      return;
    }
    if (!reason.trim()) {
      setError('Say why it is coming off.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const target =
        picked.source === 'board' ? { board_id: picked.id } : { part_instance_id: String(picked.id) };
      await woApi.removal(orderId, unit.id, { ...target, destination, reason: reason.trim() });
      toast.success(`Took ${slot} off ${unit.serial}. It went to ${DESTINATION_LABELS[destination]}.`);
      onDone();
    } catch (err) {
      setError(workOrderErrorMessage(err, 'The part was not removed. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      title={`Remove a part from ${unit.serial ?? `unit ${unit.position}`}`}
      onClose={onClose}
      size="lg"
      busy={busy}
      dirty={dirty}
      onSubmit={submit}
      problem={error}
      footer={
        <>
          <button type="button" onClick={onClose} className={buttonClass('quiet', 'md')}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !parts || parts.length === 0}
            className={buttonClass('filled', 'md')}
          >
            {busy ? 'Removing…' : 'Remove part'}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <FadeIn key={loadError ? 'error' : !parts ? 'loading' : parts.length === 0 ? 'empty' : 'parts'}>
          {loadError ? (
            <>
              {PART_HEADING}
              <Alert tone="error">
                {loadError}
              </Alert>
            </>
          ) : !parts ? (
            <>
              {PART_HEADING}
              <div className="space-y-2" aria-busy="true" aria-label="Loading fitted parts">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-11 w-full rounded-md" />
                ))}
              </div>
            </>
          ) : parts.length === 0 ? (
            <>
              {PART_HEADING}
              <p className="text-sm text-on-surface-variant">
                Nothing is recorded as fitted to this drone. Scan its parts at the assembly station first.
              </p>
            </>
          ) : (
            <RadioGroup
              label="Which part?"
              name="removal-part"
              value={slot}
              onChange={(v) => {
                setSlot(v);
                setError(null);
              }}
            >
              {parts.map(([s, p]) => (
                <RadioRow key={s} value={s}>
                  <span className="flex items-center gap-3">
                    <span className="w-10 shrink-0 text-sm font-semibold text-on-surface">{s}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-mono text-sm text-on-surface">{p.label}</span>
                      {p.display_name && (
                        <span className="block truncate text-xs text-on-surface-variant">{p.display_name}</span>
                      )}
                    </span>
                  </span>
                </RadioRow>
              ))}
            </RadioGroup>
          )}
        </FadeIn>

        <div>
          <div className="mb-2 text-sm font-medium text-on-surface">Where does it go?</div>
          <Segmented
            label="Where the part goes"
            options={DESTINATIONS.map((d) => ({ key: d, label: DESTINATION_LABELS[d] }))}
            value={destination}
            onChange={(k) => setDestination(k as RemovalDestination)}
          />
          <p className="mt-1.5 text-xs text-on-surface-variant">{DESTINATION_HINTS[destination]}</p>
        </div>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-on-surface">Why is it coming off?</span>
          <TextArea
            rows={3}
            value={reason}
            maxLength={1000}
            placeholder="ESC failed the motor test"
            onChange={(e) => {
              setReason(e.target.value);
              setError(null);
            }}
          />
        </label>
      </div>
    </Dialog>
  );
};

export default RemovalDialog;
